import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, Unplug } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const withAuth = { withCredentials: true };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");

const Field = ({ label, hint, ...props }) => (
  <label className="block">
    <span className="text-[0.62rem] uppercase tracking-[0.22em] text-black/55">{label}</span>
    <input {...props} className="mt-2 w-full h-12 bg-white/70 border border-black/15 px-4 text-sm focus:outline-none focus:border-[#BA8D53]" />
    {hint && <span className="block mt-1.5 text-xs text-black/45">{hint}</span>}
  </label>
);

export default function AdminInstagram() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ access_token: "", app_secret: "", app_id: "", mode: "instagram_login" });
  const [saving, setSaving] = useState(false);

  const load = () => axios.get(`${API}/admin/instagram`, withAuth).then((r) => setStatus(r.data)).catch(() => toast.error("Could not load Instagram settings"));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/instagram`, form, withAuth);
      if (data.ok) toast.success(`Connected — ${data.count} posts loaded`);
      else toast.error(data.error || "Saved, but Instagram did not return posts yet");
      setForm((f) => ({ ...f, access_token: "", app_secret: "" }));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const refresh = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/instagram/refresh`, {}, withAuth);
      data.ok ? toast.success(`Feed refreshed — ${data.count} posts`) : toast.error(data.error || "Refresh failed");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Refresh failed"); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Instagram? The homepage will fall back to your curated images.")) return;
    await axios.delete(`${API}/admin/instagram`, withAuth);
    toast.success("Instagram disconnected");
    load();
  };

  if (!status) return <div className="mt-12"><Loader2 className="animate-spin text-[#BA8D53]" size={24} /></div>;

  return (
    <div className="mt-12 grid lg:grid-cols-2 gap-12" data-testid="admin-instagram-section">
      <div>
        <h2 className="font-serif-brand text-3xl font-light">Live Instagram Feed</h2>
        <p className="mt-3 text-sm text-black/55 leading-relaxed max-w-md">
          Paste a long-lived Meta access token and the homepage strip switches to your real posts automatically. Tokens are renewed ~15 days before expiry; posts are cached for one hour.
        </p>
        <div className="mt-8 border border-black/10 bg-white/50 p-6 text-sm space-y-2" data-testid="instagram-status">
          <p><span className="text-black/50">Status:</span> <span data-testid="instagram-status-value" className={status.configured ? "text-green-700" : "text-black/70"}>{status.configured ? "Connected" : "Not connected — using curated images"}</span></p>
          {status.configured && (
            <>
              <p><span className="text-black/50">Login type:</span> {status.mode === "facebook_login" ? "Facebook Login" : "Instagram Login"}</p>
              <p><span className="text-black/50">Token:</span> <code className="text-xs">{status.token_preview}</code> {status.has_app_secret ? "· app secret saved" : "· no app secret (no auto-renewal)"}</p>
              <p><span className="text-black/50">Expires:</span> {fmt(status.expires_at)}</p>
              <p><span className="text-black/50">Last fetched:</span> {fmt(status.last_fetch)} · {status.cached_count} posts cached</p>
              {status.last_error && <p className="text-red-700 text-xs" data-testid="instagram-last-error">{status.last_error}</p>}
              <div className="flex gap-2 pt-3">
                <button type="button" onClick={refresh} data-testid="instagram-refresh-button" className="btn-ghost-ink flex items-center gap-2"><RefreshCw size={13} /> Refresh now</button>
                <button type="button" onClick={disconnect} data-testid="instagram-disconnect-button" className="flex items-center gap-2 text-[0.65rem] uppercase tracking-[0.2em] text-red-700 px-4"><Unplug size={13} /> Disconnect</button>
              </div>
            </>
          )}
        </div>
      </div>

      <form onSubmit={save} className="space-y-6" data-testid="instagram-form">
        <div>
          <span className="text-[0.62rem] uppercase tracking-[0.22em] text-black/55">Login type</span>
          <div className="mt-2 flex border border-black/15 text-[0.62rem] uppercase tracking-[0.18em] w-fit">
            {[["instagram_login", "Instagram Login"], ["facebook_login", "Facebook Login"]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setForm((f) => ({ ...f, mode: k }))} data-testid={`instagram-mode-${k}`} className={`px-4 py-3 ${form.mode === k ? "bg-[#111] text-[#F7F3EE]" : "text-black/55"}`}>{l}</button>
            ))}
          </div>
          <p className="mt-1.5 text-xs text-black/45">{form.mode === "facebook_login" ? "Business account linked to a Facebook Page; needs App ID + App Secret for renewal." : "Token from 'Instagram API with Instagram Login' → User token generator."}</p>
        </div>
        <Field label="Access token" required value={form.access_token} onChange={(e) => setForm({ ...form, access_token: e.target.value })} data-testid="instagram-token-input" placeholder="IGQW… or EAAB…" />
        <Field label="App secret" value={form.app_secret} onChange={(e) => setForm({ ...form, app_secret: e.target.value })} data-testid="instagram-secret-input" hint="Meta app → Settings → Basic → App Secret. Enables automatic renewal." type="password" />
        {form.mode === "facebook_login" && <Field label="App ID" value={form.app_id} onChange={(e) => setForm({ ...form, app_id: e.target.value })} data-testid="instagram-appid-input" />}
        <button type="submit" disabled={saving} data-testid="instagram-save-button" className="btn-ink !py-4 disabled:opacity-50 flex items-center gap-2">
          {saving && <Loader2 size={14} className="animate-spin" />} Connect Instagram
        </button>
      </form>
    </div>
  );
}
